
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UseUploadHandlersProps {
  tableData: any[];
  setTableData: (data: any[]) => void;
  sortColumn: string | null;
  setSortColumn: (column: string | null) => void;
  sortDirection: 'asc' | 'desc';
  setSortDirection: (direction: 'asc' | 'desc') => void;
  stateName: string;
  selectedCountry: string;
  selectedLeague: string;
  selectedYear: string;
  saveStateMutation: any;
}

export const useUploadHandlers = ({
  tableData,
  setTableData,
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  stateName,
  selectedCountry,
  selectedLeague,
  selectedYear,
  saveStateMutation
}: UseUploadHandlersProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDataLoaded = (data: any[]) => {
    if (!data || data.length === 0) return;
    setTableData(data);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }

    const sortedData = [...tableData].sort((a, b) => {
      if (a[column] === b[column]) return 0;
      if (sortDirection === 'asc') {
        return a[column] > b[column] ? 1 : -1;
      } else {
        return a[column] < b[column] ? 1 : -1;
      }
    });

    setTableData(sortedData);
  };

  const handleSaveState = async () => {
    console.log('Save state clicked', { 
      stateName, 
      selectedCountry, 
      selectedLeague, 
      selectedYear, 
      tableDataLength: tableData.length 
    });
    
    if (!stateName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a state name",
        variant: "destructive",
      });
      return;
    }

    if (!selectedCountry || !selectedLeague || !selectedYear) {
      toast({
        title: "Error",
        description: "Please select country, league, and year",
        variant: "destructive",
      });
      return;
    }

    if (tableData.length === 0) {
      toast({
        title: "Error",
        description: "No data to save. Please upload an Excel file first",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if a state with the same name already exists
      const { data: existingState, error: checkError } = await supabase
        .from('saved_states')
        .select('id')
        .eq('name', stateName)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing state:', checkError);
        toast({
          title: "Error",
          description: "Failed to check if state already exists",
          variant: "destructive",
        });
        return;
      }

      // Check if a state with the same combination already exists (country, league, year)
      const { data: existingCombination, error: combinationError } = await supabase
        .from('saved_states')
        .select('id')
        .eq('country', selectedCountry)
        .eq('league', selectedLeague)
        .eq('year', selectedYear)
        .maybeSingle();
        
      if (combinationError) {
        console.error('Error checking existing combination:', combinationError);
        toast({
          title: "Error",
          description: "Failed to check if state combination already exists",
          variant: "destructive",
        });
        return;
      }

      // Enhance data with country/league/year information
      const enhancedData = tableData.map(item => ({
        ...item,
        country: selectedCountry,
        league: selectedLeague,
        seasonYear: selectedYear
      }));

      console.log('Existing state check:', existingState);
      console.log('Existing combination check:', existingCombination);
      
      // If either the name or the combination exists, update it
      const existingId = existingState?.id || existingCombination?.id;
      const isUpdate = !!existingId;
      
      if (isUpdate) {
        console.log('State or combination already exists, updating:', existingId);
        saveStateMutation.mutate({
          id: existingId,
          name: stateName,
          country: selectedCountry,
          league: selectedLeague,
          year: selectedYear,
          data: enhancedData,
          isUpdate: true
        });
      } else {
        // If no state with this name or combination exists, create a new one
        console.log('Creating new state');
        saveStateMutation.mutate({
          name: stateName,
          country: selectedCountry,
          league: selectedLeague,
          year: selectedYear,
          data: enhancedData,
          isUpdate: false
        });
      }
    } catch (error) {
      console.error('Error in handleSaveState:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    handleDataLoaded,
    handleSort,
    handleSaveState
  };
};
